// src/components/permissions/PermissionDetails.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  fetchPermission,
  deletePermission,
  clearPermissionError,
  clearPermissionSuccess,
} from '../../store/actions/permissionActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const PermissionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedPermission, loading, error, success } = useSelector((state) => state.permissions);

  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  // Styles object
  const styles = {
    container: {
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    
    // Header
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem',
      border: 'none',
      background: 'none',
      color: '#6b7280',
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'all 0.2s',
    },
    backButtonHover: {
      color: '#111827',
      backgroundColor: '#f3f4f6',
    },
    backIcon: {
      width: '1.5rem',
      height: '1.5rem',
    },
    title: {
      fontSize: '1.875rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
    },
    editButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#4f46e5',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    deleteButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    
    // Permission Header
    permissionHeader: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '1.5rem',
      marginBottom: '1.5rem',
    },
    permissionName: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.75rem',
      fontFamily: 'monospace',
    },
    permissionBadges: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    badge: {
      padding: '0.25rem 0.75rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    moduleBadge: {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    actionBadge: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    resourceBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    
    // Tabs
    tabsContainer: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      marginBottom: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem 0.5rem 0 0',
      padding: '0 1rem',
    },
    tab: {
      padding: '0.75rem 1.5rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    tabActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
    },
    tabHover: {
      color: '#374151',
    },
    
    // Tab Content
    tabContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0 0 0.5rem 0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '1.5rem',
    },
    
    // Overview Grid
    overviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    infoCard: {
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      padding: '1rem',
    },
    cardTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    cardIcon: {
      width: '1.25rem',
      height: '1.25rem',
      color: '#6b7280',
    },
    
    // Details List
    detailsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    detailItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    detailLabel: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '0.25rem',
    },
    detailValue: {
      fontSize: '1rem',
      color: '#111827',
      fontWeight: 500,
    },
    detailValueMonospace: {
      fontSize: '1rem',
      color: '#111827',
      fontWeight: 500,
      fontFamily: 'monospace',
    },
    
    // Description
    descriptionContainer: {
      gridColumn: 'span 2',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      padding: '1rem',
    },
    descriptionText: {
      fontSize: '0.875rem',
      color: '#374151',
      lineHeight: '1.6',
      whiteSpace: 'pre-wrap',
    },
    
    // Usage Section
    usageCard: {
      gridColumn: 'span 2',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      padding: '1rem',
    },
    rolesList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      marginTop: '1rem',
    },
    roleItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.375rem',
      border: '1px solid #e5e7eb',
    },
    roleInfo: {
      flex: 1,
    },
    roleName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    roleDescription: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    roleLink: {
      color: '#2563eb',
      textDecoration: 'none',
      fontSize: '0.875rem',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.375rem',
      transition: 'background-color 0.2s',
    },
    roleLinkHover: {
      backgroundColor: '#eff6ff',
    },
    
    // Metadata Grid
    metadataGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    metadataItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    metadataLabel: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '0.25rem',
    },
    metadataValue: {
      fontSize: '0.875rem',
      color: '#111827',
    },
    
    // Empty State
    emptyState: {
      textAlign: 'center',
      padding: '2rem',
      color: '#6b7280',
      fontSize: '0.875rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
    },
    
    // Loading Overlay
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    
    // Related Info
    relatedInfo: {
      marginTop: '0.5rem',
      padding: '0.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.375rem',
      border: '1px solid #e5e7eb',
    },
    relatedTitle: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#4b5563',
      marginBottom: '0.25rem',
      textTransform: 'uppercase',
    },
    relatedContent: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    
    // Permission Format
    formatExample: {
      marginTop: '1rem',
      padding: '0.75rem',
      backgroundColor: '#1e293b',
      color: '#e2e8f0',
      borderRadius: '0.375rem',
      fontFamily: 'monospace',
      fontSize: '0.875rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchPermission(id));
    }
  }, [dispatch, id]);

  const tabs = [
    { id: 'overview', name: 'Aperçu', icon: '📋' },
    { id: 'usage', name: 'Usage', icon: '📊' },
    { id: 'metadata', name: 'Metadata', icon: 'ℹ️' },
  ];

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deletePermission(id));
      navigate('/admin/permissions');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Parse permission name to get components
  const parsePermissionName = useCallback((name) => {
    if (!name) return { module: '', action: '', resource: '' };
    const parts = name.split(':');
    return {
      module: parts[0] || '',
      action: parts[1] || '',
      resource: parts[2] || '',
    };
  }, []);

  // Get module info
  const getModuleInfo = useCallback((module) => {
    // This would ideally come from the API
    const moduleInfo = {
      users: { description: 'Module de gestion des utilisateurs', icon: '👥' },
      roles: { description: 'Module de gestion des rôles et permissions', icon: '🎭' },
      permissions: { description: 'Module de gestion des permissions', icon: '🔑' },
      products: { description: 'Module de gestion du catalogue produits', icon: '📦' },
      blog: { description: 'Module de gestion du blog et du contenu', icon: '📝' },
      faq: { description: 'Module de gestion des questions fréquentes', icon: '❓' },
      documents: { description: 'Module de gestion des documents', icon: '📄' },
      insurance: { description: 'Module de gestion des assurances', icon: '🛡️' },
      reports: { description: 'Module de gestion des rapports et analyses', icon: '📊' },
      settings: { description: 'Paramètres système', icon: '⚙️' },
    };
    return moduleInfo[module] || { description: `${module} module`, icon: '📁' };
  }, []);

  if (loading && !selectedPermission) {
    return (
      <div style={styles.loadingOverlay}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!selectedPermission) {
    return (
      <div style={styles.container}>
        <Alert type="error" message="Permission not found" />
        <button
          onClick={() => navigate('/admin/permissions')}
          style={{ ...styles.editButton, marginTop: '1rem' }}
        >
          Back to Permissions
        </button>
      </div>
    );
  }

  const parsedName = parsePermissionName(selectedPermission.name);
  const moduleInfo = getModuleInfo(selectedPermission.module || parsedName.module);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/permissions')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = styles.backButtonHover.color;
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = styles.backButton.color;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <svg style={styles.backIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Détails de la permission</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/permissions/${id}/edit`}
            style={styles.editButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier la permission
          </Link>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={styles.deleteButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer la permission
          </button>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => dispatch(clearPermissionError())} />}
      {success && <Alert type="success" message={success} onClose={() => dispatch(clearPermissionSuccess())} />}

      {/* Permission Header */}
      <div style={styles.permissionHeader}>
        <h2 style={styles.permissionName}>{selectedPermission.name}</h2>
        <div style={styles.permissionBadges}>
          <span style={{ ...styles.badge, ...styles.moduleBadge }}>
            {selectedPermission.module || parsedName.module}
          </span>
          <span style={{ ...styles.badge, ...styles.actionBadge }}>
            {selectedPermission.action || parsedName.action}
          </span>
          {(selectedPermission.resource || parsedName.resource) && (
            <span style={{ ...styles.badge, ...styles.resourceBadge }}>
              {selectedPermission.resource || parsedName.resource}
            </span>
          )}
        </div>
        {selectedPermission.description && (
          <p style={{ fontSize: '1rem', color: '#4b5563', marginTop: '0.5rem' }}>
            {selectedPermission.description}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = styles.tabHover.color;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = styles.tab.color;
              }
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            <span style={{ marginRight: '0.5rem' }}>{tab.icon}</span>
            {tab.name}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {activeTab === 'overview' && (
          <div style={styles.overviewGrid}>
            {/* Module Information */}
            <div style={styles.infoCard}>
              <h3 style={styles.cardTitle}>
                <svg style={styles.cardIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                Informations du module
              </h3>
              <div style={styles.detailsList}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Nom du module</span>
                  <span style={styles.detailValue}>{selectedPermission.module || parsedName.module}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Icône du module</span>
                  <span style={styles.detailValue}>{moduleInfo.icon}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Description</span>
                  <span style={styles.detailValue}>{moduleInfo.description}</span>
                </div>
              </div>
            </div>

            {/* Action Information */}
            <div style={styles.infoCard}>
              <h3 style={styles.cardTitle}>
                <svg style={styles.cardIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Informations sur l'action
              </h3>
              <div style={styles.detailsList}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Action</span>
                  <span style={styles.detailValue}>{selectedPermission.action || parsedName.action}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Ressource</span>
                  <span style={styles.detailValue}>{selectedPermission.resource || parsedName.resource || 'Toutes les ressources'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Format de la permission</span>
                  <div style={styles.formatExample}>
                    {selectedPermission.name}
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div style={styles.descriptionContainer}>
              <h3 style={styles.cardTitle}>
                <svg style={styles.cardIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                Description
              </h3>
              <div style={styles.descriptionText}>
                {selectedPermission.description || 'Aucune description fournie pour cette permission.'}
              </div>
            </div>

            {/* Permission Format Explanation */}
            <div style={{ gridColumn: 'span 2' }}>
              <div style={styles.relatedInfo}>
                <div style={styles.relatedTitle}>Explication du format de la permission</div>
                <div style={styles.relatedContent}>
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Format:</strong> <code style={{ backgroundColor: '#f3f4f6', padding: '0.125rem 0.25rem', borderRadius: '0.25rem' }}>module:action:resource</code>
                  </p>
                  <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
                    <li><strong>module</strong>: Le module de l'application (e.g., users, roles, products)</li>
                    <li><strong>action</strong>: Ce qui peut être fait (e.g., create, read, update, delete, manage)</li>
                    <li><strong>resource</strong> (optionnel): Le type spécifique de ressource dans le module</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'usage' && (
          <div style={styles.usageCard}>
            <h3 style={styles.cardTitle}>
              <svg style={styles.cardIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Rôles utilisant cette permission
            </h3>
            
            {selectedPermission.roles && selectedPermission.roles.length > 0 ? (
              <div style={styles.rolesList}>
                {selectedPermission.roles.map((role) => (
                  <div key={role.id} style={styles.roleItem}>
                    <div style={styles.roleInfo}>
                      <div style={styles.roleName}>{role.name}</div>
                      {role.description && (
                        <div style={styles.roleDescription}>{role.description}</div>
                      )}
                    </div>
                    <Link
                      to={`/admin/roles/${role.id}`}
                      style={styles.roleLink}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = styles.roleLinkHover.backgroundColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Voir le rôle
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>
                <svg style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: '#9ca3af' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <p>Cette permission n'est utilisée par aucun rôle pour le moment.</p>
              </div>
            )}

            {/* Usage Statistics */}
            <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div style={styles.relatedInfo}>
                <div style={styles.relatedTitle}>Total des Roles</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
                  {selectedPermission.roles?.length || 0}
                </div>
              </div>
              <div style={styles.relatedInfo}>
                <div style={styles.relatedTitle}>Total des Utilisateurs</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
                  {selectedPermission.roles?.reduce((acc, role) => acc + (role._count?.userRoles || 0), 0) || 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'metadata' && (
          <div>
            <h3 style={{ ...styles.cardTitle, marginBottom: '1rem' }}>Metadata</h3>
            <div style={styles.metadataGrid}>
              <div style={styles.metadataItem}>
                <span style={styles.metadataLabel}>ID de la permission</span>
                <span style={styles.metadataValue}>{selectedPermission.id}</span>
              </div>
              <div style={styles.metadataItem}>
                <span style={styles.metadataLabel}>Créé le</span>
                <span style={styles.metadataValue}>{formatDate(selectedPermission.createdAt)}</span>
              </div>
              <div style={styles.metadataItem}>
                <span style={styles.metadataLabel}>Dernière mise à jour</span>
                <span style={styles.metadataValue}>{formatDate(selectedPermission.updatedAt)}</span>
              </div>
            </div>

            {/* System Information */}
            <div style={{ marginTop: '2rem' }}>
              <div style={styles.relatedInfo}>
                <div style={styles.relatedTitle}>Information Système</div>
                <div style={styles.relatedContent}>
                  <p>Cette permission est utilisée pour le contrôle d'accès dans le système RBAC.</p>
                  <p style={{ marginTop: '0.5rem' }}>
                    <strong>Chaîne de permission:</strong> <code style={{ backgroundColor: '#f3f4f6', padding: '0.125rem 0.25rem', borderRadius: '0.25rem' }}>{selectedPermission.name}</code>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer la permission"
        message={`Êtes-vous sûr de vouloir supprimer la permission "${selectedPermission.name}" ? Cette action ne peut pas être annulée.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default PermissionDetails;